import React from "react";
import { Container, Box, Typography, Divider, Link as MuiLink } from "@mui/material";

const COLORS = {
  heading: "#0A0F2C",
  accent: "#1E3A8A",
  text: "#334155",
  subtle: "#64748B",
};

function Section({ id, title, children }) {
  return (
    <Box id={id} sx={{ mb: 5, scrollMarginTop: "100px" }}>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          color: COLORS.heading,
          mb: 1.5,
          pb: 1,
          borderBottom: "1px solid #E2E8F0",
        }}
      >
        {title}
      </Typography>
      <Typography
        component="div"
        sx={{ color: COLORS.text, fontSize: "0.95rem", lineHeight: 1.85 }}
      >
        {children}
      </Typography>
    </Box>
  );
}

const TOC = [
  ["taxes", "1. Taxes"],
  ["activation", "2. Activation of Services"],
  ["saas", "3. SaaS Platform"],
  ["scope", "4. Subscription Scope"],
  ["limits", "5. Plan Limits and Usage"],
  ["non-refundable", "6. Non-Refundable Payments"],
  ["no-cancellation", "7. No Cancellation After Payment"],
  ["no-transfer", "8. No Transfer or Exchange"],
  ["expiry", "9. Expiry of Unused Entitlements"],
  ["billing-info", "10. Customer Billing Information"],
  ["refund-policy", "11. Refund & Cancellation Policy"],
  ["ai-features", "12. AI-Powered Features"],
  ["hiring-responsibility", "13. Customer Responsibility for Hiring Decisions"],
  ["ai-accuracy", "14. Accuracy of AI Outputs"],
  ["candidate-data", "15. Candidate Data and Lawful Use"],
  ["communication-services", "16. Communication Services"],
  ["third-party", "17. Third-Party Services"],
  ["acceptable-use", "18. Acceptable Use"],
  ["account-security", "19. Account Security"],
  ["availability", "20. Service Availability"],
  ["support", "21. Technical Support"],
  ["plan-changes", "22. Plan and Feature Changes"],
  ["suspension", "23. Suspension or Termination for Misuse"],
  ["statutory", "24. Statutory Charges"],
  ["liability", "25. Limitation of Liability"],
  ["no-guarantee", "26. No Employment Guarantee"],
  ["e-invoice", "27. Electronic Invoice"],
  ["governing-law", "28. Governing Law and Jurisdiction"],
  ["acceptance", "29. Acceptance of Terms"],
];

function TermsAndConditions() {
  return (
    <Box sx={{ background: "linear-gradient(180deg, #F8FAFC 0%, #FFFFFF 280px)" }}>
      <Container maxWidth="md" sx={{ py: { xs: 5, md: 8 } }}>
        <Box
          sx={{
            width: 56,
            height: 5,
            borderRadius: 3,
            background: `linear-gradient(90deg, ${COLORS.accent}, #3B82F6)`,
            mb: 2.5,
          }}
        />
        <Typography
          sx={{
            color: COLORS.accent,
            fontWeight: 700,
            letterSpacing: 1,
            fontSize: "0.8rem",
            textTransform: "uppercase",
            mb: 1,
          }}
        >
          Evolve X Innovations &middot; ExiHire
        </Typography>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 800,
            color: COLORS.heading,
            fontSize: { xs: "1.8rem", md: "2.6rem" },
            mb: 1,
          }}
        >
          Terms &amp; Conditions
        </Typography>
        <Typography sx={{ color: COLORS.subtle, mb: 3, fontSize: "0.9rem" }}>
          Effective Date: July 8, 2026 &nbsp;&middot;&nbsp; Last Updated: July 8, 2026
        </Typography>

        <Typography sx={{ color: COLORS.text, fontSize: "0.98rem", lineHeight: 1.85, mb: 5 }}>
          These Terms &amp; Conditions (&ldquo;Terms&rdquo;) govern access to and use of{" "}
          <strong>ExiHire</strong>, a cloud-based Software-as-a-Service (SaaS) recruitment, applicant
          tracking, candidate assessment, communication, and AI-enabled hiring platform operated by
          EVOLVE X INNOVATIONS (&ldquo;EVOLVE X INNOVATIONS&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;,
          or &ldquo;our&rdquo;). By purchasing, subscribing to, accessing, or using ExiHire, you agree to
          be bound by these Terms.
        </Typography>

        <Box
          sx={{
            display: { xs: "none", md: "grid" },
            gridTemplateColumns: "1fr 1fr",
            columnGap: 3,
            rowGap: 0.75,
            p: 3,
            mb: 6,
            border: "1px solid #E2E8F0",
            borderRadius: 3,
            bgcolor: "#F8FAFC",
          }}
        >
          {TOC.map(([id, label]) => (
            <MuiLink
              key={id}
              href={`#${id}`}
              underline="hover"
              sx={{ color: COLORS.text, fontSize: "0.88rem", py: 0.4, "&:hover": { color: COLORS.accent } }}
            >
              {label}
            </MuiLink>
          ))}
        </Box>

        <Section id="taxes" title="1. Taxes">
          Prices mentioned are exclusive of GST unless explicitly stated otherwise. GST shall be charged
          as applicable under the Goods and Services Tax laws in India. For customers registered in
          Andhra Pradesh, CGST at 9% and SGST at 9% shall apply; for customers registered in other Indian
          States or Union Territories, IGST at 18% shall apply, subject to applicable GST laws and
          place-of-supply requirements. GST is calculated on the actual payable selling price of a plan
          (for example, a discounted launch-offer price), not on any struck-through reference or original
          price shown for comparison, based on the recruiter&rsquo;s registered company State as
          maintained in their ExiHire profile. Recruiters are responsible for ensuring their registered
          State is accurate before completing a paid subscription purchase.
        </Section>

        <Section id="activation" title="2. Activation of Services">
          ExiHire subscriptions, platform access, usage credits, communication credits, AI interview
          credits, and other purchased services shall be activated only after successful payment
          confirmation and receipt of cleared funds by EVOLVE X INNOVATIONS.
        </Section>

        <Section id="saas" title="3. SaaS Platform">
          ExiHire is a cloud-based Software-as-a-Service (SaaS) recruitment and hiring platform. Access
          is provided on a subscription, usage-based, custom enterprise, or other commercially agreed
          basis and remains valid only for the purchased or contractually agreed duration.
        </Section>

        <Section id="scope" title="4. Subscription Scope">
          Access to ExiHire features, including but not limited to job postings, candidate applications,
          AI-powered shortlisting, AI interviews, assessments, candidate outreach, communication services,
          recruitment workflows, reports, analytics, and other platform capabilities, shall be governed
          by the limits and entitlements of the purchased plan.
        </Section>

        <Section id="limits" title="5. Plan Limits and Usage">
          Job posting limits, application limits, AI interview limits, assessment limits, calling
          credits, WhatsApp credits, communication credits, user seats, storage limits, and other usage
          entitlements may vary by subscription plan or commercial agreement. Usage beyond the included
          limits may require the purchase of additional credits, add-ons, or plan upgrades.
        </Section>

        <Section id="non-refundable" title="6. Non-Refundable Payments">
          All subscription fees, platform credits, communication credits, AI interview credits,
          implementation charges, onboarding fees, setup fees, custom development charges, enterprise
          plan fees, and any other payments made to EVOLVE X INNOVATIONS are non-refundable and
          non-transferable, except where required under applicable law.
        </Section>

        <Section id="no-cancellation" title="7. No Cancellation After Payment">
          No cancellation is permitted after successful payment confirmation and activation of the
          applicable subscription, credit package, add-on, custom plan, or enterprise plan. Once
          activated, the purchase cannot be cancelled, reversed, or converted into cash.
        </Section>

        <Section id="no-transfer" title="8. No Transfer or Exchange">
          Purchased subscriptions, platform licenses, job posting limits, application limits, AI
          interview credits, communication credits, calling credits, WhatsApp credits, or other
          entitlements cannot be exchanged for cash, transferred to another company or institution, or
          adjusted against future purchases unless explicitly approved in writing by EVOLVE X
          INNOVATIONS.
        </Section>

        <Section id="expiry" title="9. Expiry of Unused Entitlements">
          Any unused credits, job posting limits, application limits, AI interview credits, communication
          credits, licenses, add-ons, or remaining subscription validity shall expire in accordance with
          the applicable purchased plan, validity period, or commercial agreement and shall not be
          eligible for refund, carry-forward, or extension unless explicitly stated otherwise in writing.
        </Section>

        <Section id="billing-info" title="10. Customer Billing Information">
          The customer is responsible for providing accurate and complete billing information, including
          legal entity name, GSTIN where applicable, billing address, authorized contact details, and any
          other information required before invoice generation.
        </Section>

        <Section id="refund-policy" title="11. Refund & Cancellation Policy">
          All payments made towards ExiHire subscriptions, custom plans, enterprise plans, implementation
          services, onboarding services, platform credits, AI interview credits, assessments,
          communication credits, add-ons, or any other services are final. No refunds, no cancellations,
          and no transfers are permitted after successful payment, except where required under applicable
          law.
        </Section>

        <Section id="ai-features" title="12. AI-Powered Features">
          ExiHire may use artificial intelligence and automated systems for candidate matching,
          shortlisting assistance, interview generation, response analysis, evaluations, scoring,
          summaries, reports, recommendations, and other recruitment-related functions. AI-generated
          outputs are intended to support and assist recruitment decisions and should not be treated as
          the sole basis for final hiring, rejection, employment, or other high-impact decisions.
        </Section>

        <Section id="hiring-responsibility" title="13. Customer Responsibility for Hiring Decisions">
          The customer remains solely responsible for reviewing candidate information, verifying
          qualifications and credentials, conducting appropriate due diligence, ensuring lawful
          recruitment practices, and making final hiring or rejection decisions. EVOLVE X INNOVATIONS
          does not guarantee the suitability, performance, authenticity, employability, or future conduct
          of any candidate.
        </Section>

        <Section id="ai-accuracy" title="14. Accuracy of AI Outputs">
          While reasonable efforts are made to improve the quality and reliability of AI-powered
          features, EVOLVE X INNOVATIONS does not warrant that AI-generated questions, scores,
          evaluations, rankings, summaries, recommendations, reports, or other outputs will always be
          complete, accurate, error-free, unbiased, or suitable for every hiring context.
        </Section>

        <Section id="candidate-data" title="15. Candidate Data and Lawful Use">
          The customer is responsible for ensuring that candidate data, resumes, contact information,
          interview data, assessment information, and other personal data uploaded, collected,
          processed, or used through ExiHire are handled with appropriate authority, notice, consent, or
          other lawful basis as required under applicable law. Use of the Service is also governed by our{" "}
          <MuiLink href="/privacy-policy" sx={{ color: COLORS.accent, fontWeight: 600 }}>
            Privacy Policy
          </MuiLink>
          .
        </Section>

        <Section id="communication-services" title="16. Communication Services">
          Email, SMS, WhatsApp, voice calling, and other communication features may depend on third-party
          service providers, telecom networks, messaging platforms, email providers, and communication
          infrastructure. Delivery, availability, approval, timing, and successful receipt of
          communications are not guaranteed and may be subject to third-party policies and restrictions.
        </Section>

        <Section id="third-party" title="17. Third-Party Services">
          Certain ExiHire features may integrate with or depend upon third-party services, including
          Razorpay and other payment gateways, cloud infrastructure, email services, telecommunication
          providers, messaging platforms, AI service providers, and other external systems. EVOLVE X
          INNOVATIONS shall not be responsible for service interruptions, policy changes, delivery
          failures, outages, restrictions, suspensions, or other issues directly caused by such
          third-party providers. Payments are processed through Razorpay; ExiHire does not store your
          card, UPI, or net-banking credentials.
        </Section>

        <Section id="acceptable-use" title="18. Acceptable Use">
          Customers shall not misuse ExiHire for unlawful recruitment practices, discrimination
          prohibited by applicable law, spam, harassment, fraudulent job postings, impersonation,
          unauthorized data collection, security attacks, platform abuse, or any activity that violates
          applicable laws, regulations, or third-party rights.
        </Section>

        <Section id="account-security" title="19. Account Security">
          The customer is responsible for maintaining the confidentiality and security of account
          credentials, user access, administrator permissions, and activities conducted through its
          ExiHire account. Any suspected unauthorized access should be reported promptly.
        </Section>

        <Section id="availability" title="20. Service Availability">
          EVOLVE X INNOVATIONS will use commercially reasonable efforts to maintain platform
          availability. However, uninterrupted or error-free access is not guaranteed. Temporary
          interruptions may occur due to maintenance, upgrades, security requirements, technical
          failures, third-party dependencies, or circumstances beyond reasonable control.
        </Section>

        <Section id="support" title="21. Technical Support">
          Technical support shall be provided during official business hours and in accordance with the
          support scope, service level, and entitlements of the subscribed plan or applicable commercial
          agreement.
        </Section>

        <Section id="plan-changes" title="22. Plan and Feature Changes">
          EVOLVE X INNOVATIONS reserves the right to update, modify, improve, replace, discontinue, or
          restructure platform features, usage limits, credit structures, integrations, and service
          offerings. Material changes affecting an active paid subscription shall be handled in
          accordance with the applicable commercial agreement and applicable law.
        </Section>

        <Section id="suspension" title="23. Suspension or Termination for Misuse">
          EVOLVE X INNOVATIONS reserves the right to suspend or restrict access to ExiHire where there is
          suspected fraud, payment default, unlawful activity, security risk, platform abuse, violation
          of applicable terms, or misuse that may harm the platform, its users, candidates, or third
          parties.
        </Section>

        <Section id="statutory" title="24. Statutory Charges">
          Any statutory taxes, duties, levies, cess, or government charges introduced or made applicable
          after the date of invoice shall be payable by the customer wherever applicable.
        </Section>

        <Section id="liability" title="25. Limitation of Liability">
          To the maximum extent permitted by applicable law, EVOLVE X INNOVATIONS shall not be liable for
          indirect, incidental, special, consequential, or business losses arising from the use of
          ExiHire, including loss of business opportunity, hiring outcomes, candidate decisions,
          communication failures, third-party service interruptions, or reliance on AI-generated outputs.
        </Section>

        <Section id="no-guarantee" title="26. No Employment Guarantee">
          Use of ExiHire does not guarantee candidate availability, successful hiring, employee retention,
          recruitment outcomes, interview performance, job acceptance, or any specific business result.
        </Section>

        <Section id="e-invoice" title="27. Electronic Invoice">
          Any invoice issued electronically is a computer-generated document and does not require a
          physical signature, subject to applicable legal and tax requirements.
        </Section>

        <Section id="governing-law" title="28. Governing Law and Jurisdiction">
          These Terms and any transaction relating to ExiHire shall be governed by the laws of India. All
          disputes arising from or relating to the transaction, subscription, services, or use of ExiHire
          shall be subject to the exclusive jurisdiction of the competent courts at Vijayawada, Andhra
          Pradesh, India.
        </Section>

        <Section id="acceptance" title="29. Acceptance of Terms">
          By purchasing, subscribing to, accessing, or using ExiHire services, the customer acknowledges
          that they have read, understood, and agreed to these Terms &amp; Conditions, together with any
          applicable order form, quotation, invoice, proposal, plan details,{" "}
          <MuiLink href="/privacy-policy" sx={{ color: COLORS.accent, fontWeight: 600 }}>
            Privacy Policy
          </MuiLink>
          , or separate written agreement executed with EVOLVE X INNOVATIONS.
        </Section>

        <Divider sx={{ my: 4 }} />
        <Typography sx={{ color: COLORS.subtle, fontSize: "0.85rem", textAlign: "center" }}>
          Questions about these Terms? Contact us at{" "}
          <MuiLink href="mailto:connect@exihire.com" sx={{ color: COLORS.accent, fontWeight: 600 }}>
            connect@exihire.com
          </MuiLink>
        </Typography>
      </Container>
    </Box>
  );
}

export default TermsAndConditions;
